import { DevTool } from "@hookform/devtools";
import { FC, PropsWithChildren, useEffect, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import styled from "styled-components";

import {
  useConditionalFormRevalidate,
  useCourseStationActions,
  useCrudEditContext,
  useYupValidationResolver,
} from "@ryde/redux";
import {
  courseEditHasMetadataChangedAtom,
  CourseFormFields,
  courseHasUnsavedStationChangeAtom,
  CourseMetadataKeys,
  coursePotentialPassengersAtom,
  DATA_E2E_KEY,
  editFormDataAF,
  EditFormMode,
  getNewCourseTemplate,
  ICourseDto,
  IExtendableDto,
  isEqualNumberArraysAnyOrder,
  IStyledAttributes,
  KeyboardKeys,
  Log,
  logBoolean,
  shouldDebugForms,
  shouldResetOnAddNew,
  shouldResetOnEditOrDuplicate,
  useIsMobile,
  useSystemParams,
  useTimezone,
  yupValidationSchemaSF,
} from "@ryde/shared";
import { rydeColors } from "@ryde/theme";

const FormTagCourseStyled = styled.form.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "form-tag",
})`
  background-color: ${rydeColors.white};
`;

export const CoursesEditFormTagWrapper: FC<PropsWithChildren> = ({
  children,
}) => {
  const {
    data: { dto, isEdit, isDuplicate },
    entity,
    mode,
  } = useCrudEditContext<ICourseDto>();
  const { isMobile } = useIsMobile();
  const { curDateTimeISO } = useTimezone();
  const yupSchemaDynamic = useRecoilValue(yupValidationSchemaSF(entity));
  const yupDynamicResolver = useYupValidationResolver(
    // `undefined`, not a fresh `{}` - a new literal every render recreates the resolver.
    dto ? yupSchemaDynamic : undefined,
    entity,
  );
  const methods = useForm<IExtendableDto>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    resolver: yupDynamicResolver,
  });

  const {
    reset,
    clearErrors,
    control,
    getValues,
    setValue,
    trigger,
    formState: { isDirty },
  } = methods;

  const { fetchPotentialStationsPassengers } = useCourseStationActions();
  const { enableCoursePotentialPassengersCount } = useSystemParams();
  const isCoursePotentialPassengersCountEnabled =
    enableCoursePotentialPassengersCount !== false;
  const potentialPassengersStationsLastFetch = useRef<number[]>([]);

  const [courseHasUnsavedStationChange, setCourseHasUnsavedStationChange] =
    useRecoilState(courseHasUnsavedStationChangeAtom);
  const [courseEditHasMetadataChanged, setCourseEditHasMetadataChanged] =
    useRecoilState(courseEditHasMetadataChangedAtom);
  const setCoursePotentialPassengers = useSetRecoilState(
    coursePotentialPassengersAtom,
  );

  const setCourseDto = useSetRecoilState(
    editFormDataAF([entity, mode || EditFormMode.CrudRoute]),
  );
  const courseDto = useRecoilValue(
    editFormDataAF([entity, mode || EditFormMode.CrudRoute]),
  );

  // Recoil course metadata is updated on drag/recount before the form; keep route polyline in sync
  // so MapLibrePolylineEdit does not keep drawing a stale encoded path from react-hook-form.
  useEffect(() => {
    if (getValues(CourseFormFields.isManualPolylineEdit)) {
      return;
    }

    const recoilPolyline =
      courseDto?.metadata?.[CourseMetadataKeys.routePolyline];
    if (getValues(CourseFormFields.routePolyline) !== recoilPolyline) {
      setValue(CourseFormFields.routePolyline, recoilPolyline, {
        shouldDirty: true,
        shouldTouch: true,
      });
    }

    const recoilManualPolylineEdit =
      courseDto?.metadata?.[CourseMetadataKeys.isManualPolylineEdit];
    if (
      getValues(CourseFormFields.isManualPolylineEdit) !==
      recoilManualPolylineEdit
    ) {
      setValue(
        CourseFormFields.isManualPolylineEdit,
        recoilManualPolylineEdit,
        {
          shouldDirty: true,
          shouldTouch: true,
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync only route-polyline metadata fields
  }, [
    courseDto?.metadata?.[CourseMetadataKeys.isManualPolylineEdit],
    courseDto?.metadata?.[CourseMetadataKeys.routePolyline],
    getValues,
    setValue,
  ]);

  useEffect(() => {
    /* initial cleanup of previous form state values  */
    setCourseHasUnsavedStationChange(false);
    setCourseEditHasMetadataChanged(false);
    setCoursePotentialPassengers(undefined);

    potentialPassengersStationsLastFetch.current = [];
  }, [
    setCourseEditHasMetadataChanged,
    setCourseHasUnsavedStationChange,
    setCoursePotentialPassengers,
  ]);

  useEffect(() => {
    if (!isEdit && !isDuplicate && dto === undefined) {
      setCourseDto(getNewCourseTemplate(curDateTimeISO));
    }
  }, [curDateTimeISO, dto, isDuplicate, isEdit, setCourseDto]);

  useEffect(() => {
    if (
      isDirty ||
      courseHasUnsavedStationChange ||
      courseEditHasMetadataChanged
    ) {
      Log.debug(
        `Course dto changed without form reset, isDirty: ${logBoolean(
          isDirty,
        )}, metadata changed ${logBoolean(
          courseEditHasMetadataChanged,
        )}, has changed stations: ${logBoolean(
          courseHasUnsavedStationChange,
        )}}`,
      );
      return;
    }

    if (
      shouldResetOnEditOrDuplicate({ dto, isEdit, isDuplicate }) ||
      shouldResetOnAddNew({ dto, isEdit, isDuplicate })
    ) {
      Log.debug(`form reset called for entity ${entity}, dto:`, dto);
      clearErrors();
      reset({ ...dto });
    }
  }, [
    clearErrors,
    courseEditHasMetadataChanged,
    courseHasUnsavedStationChange,
    dto,
    entity,
    isDirty,
    isDuplicate,
    isEdit,
    reset,
  ]);

  useConditionalFormRevalidate({
    trigger,
    isEdit,
    dto,
    yupSchemaDynamic,
    getValues,
    clearErrors,
  });

  useEffect(() => {
    const stationIdsToFetch =
      dto?.metadata?.stationsInfo
        ?.map(station => station.id)
        .filter((id): id is number => Number.isFinite(id) && id > 0) ?? [];

    if (!isCoursePotentialPassengersCountEnabled) {
      potentialPassengersStationsLastFetch.current = [];
      setCoursePotentialPassengers(undefined);
      return;
    }

    if (
      !isMobile &&
      stationIdsToFetch.length &&
      !isEqualNumberArraysAnyOrder(
        potentialPassengersStationsLastFetch.current,
        stationIdsToFetch,
      )
    ) {
      potentialPassengersStationsLastFetch.current = stationIdsToFetch;
      fetchPotentialStationsPassengers(stationIdsToFetch);
    }
  }, [
    dto?.metadata?.stationsInfo,
    fetchPotentialStationsPassengers,
    isCoursePotentialPassengersCountEnabled,
    isMobile,
    setCoursePotentialPassengers,
  ]);

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
  };

  const handleKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === KeyboardKeys.ENTER) {
      event.preventDefault();
    }
  };

  return (
    <FormProvider {...methods}>
      <FormTagCourseStyled
        onSubmit={handleFormSubmit}
        onKeyDown={handleKeyDown}
        noValidate
        data-e2e-entity={entity}
        data-e2e-mode={mode}
      >
        {children}
      </FormTagCourseStyled>
      {shouldDebugForms() && <DevTool control={control} />}
    </FormProvider>
  );
};
